import type { LatLng } from "./types.js";

const EARTH_RADIUS_KM = 6371;

/** Great-circle distance in kilometers. */
export function haversineKm(a: LatLng, b: LatLng): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function centroid(points: LatLng[]): LatLng | null {
  if (points.length === 0) return null;
  const sum = points.reduce(
    (acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }),
    { lat: 0, lng: 0 }
  );
  return { lat: sum.lat / points.length, lng: sum.lng / points.length };
}

/**
 * Greedy geographic clustering: each point joins the first cluster whose
 * centroid is within `radiusKm`, otherwise starts a new cluster.
 */
export function clusterByProximity<T extends LatLng>(
  items: T[],
  radiusKm: number
): T[][] {
  if (items.length === 0) return [];
  const clusters: { center: LatLng; members: T[] }[] = [];

  for (const item of items) {
    let placed = false;
    for (const cluster of clusters) {
      if (haversineKm(item, cluster.center) <= radiusKm) {
        cluster.members.push(item);
        const next = centroid(cluster.members);
        if (next) cluster.center = next;
        placed = true;
        break;
      }
    }
    if (!placed) {
      clusters.push({ center: { lat: item.lat, lng: item.lng }, members: [item] });
    }
  }

  return clusters.map((c) => c.members);
}

/** Deterministic pseudo-coordinates for dev/test when geocoding is unavailable. */
export function pseudoGeocodeFromAddress(address: string): LatLng {
  let h = 0;
  const normalized = address.trim().toLowerCase();
  for (let i = 0; i < normalized.length; i++) {
    h = (h * 31 + normalized.charCodeAt(i)) >>> 0;
  }
  const lat = 33 + (h % 1000) / 100;
  const lng = -97 - ((h >> 10) % 800) / 100;
  return { lat, lng };
}
